import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import type { Motor } from '@noumase/cylinder-calc';
import { useMotorLibrary, type MotorDraft } from './motorStore';

const { Text } = Typography;

interface MotorFormValues {
  brand: string;
  series?: string;
  model?: string;
  frameSize?: string;
  power: number;
  ratedTorque: number;
  ratedSpeed: number;
  hasBrakeOption: boolean;
  cpqMotorBrandCode: string;
  cpqMotorPowerCode: string;
}

const EMPTY_FORM: MotorFormValues = {
  brand: '',
  series: '',
  model: '',
  frameSize: '',
  power: 1000,
  ratedTorque: 3.18,
  ratedSpeed: 3000,
  hasBrakeOption: true,
  cpqMotorBrandCode: '',
  cpqMotorPowerCode: '',
};

function formatPower(w: number): string {
  return w >= 1000 ? `${(w / 1000).toString().replace(/\.?0+$/, '')}KW` : `${w}W`;
}

export default function MotorLibraryPanel() {
  const { t } = useTranslation();
  const { motors, customized, add, update, remove, resetDefault, exportJson } =
    useMotorLibrary();
  const [editing, setEditing] = useState<Motor | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<MotorFormValues>();
  const [messageApi, messageCtx] = message.useMessage();

  const openCreate = () => {
    form.resetFields();
    form.setFieldsValue(EMPTY_FORM);
    setCreating(true);
  };

  const openEdit = (m: Motor) => {
    form.resetFields();
    form.setFieldsValue({
      brand: m.brand,
      series: m.series ?? '',
      model: m.model ?? '',
      frameSize: m.frameSize ?? '',
      power: m.power,
      ratedTorque: m.ratedTorque,
      ratedSpeed: m.ratedSpeed,
      hasBrakeOption: m.hasBrakeOption,
      cpqMotorBrandCode: m.cpqMotorBrandCode,
      cpqMotorPowerCode: m.cpqMotorPowerCode,
    });
    setEditing(m);
  };

  const closeModal = () => {
    setEditing(null);
    setCreating(false);
  };

  const handleSubmit = (values: MotorFormValues) => {
    const brand = values.brand.trim();
    const series = (values.series ?? '').trim();
    const model = (values.model ?? '').trim();
    const frameSize = (values.frameSize ?? '').trim();
    // 业务唯一性：仅 model 非空时才校验 (brand, model)；空型号允许重复（占位
    // 电机 / 待补型号场景）
    if (model) {
      const dup = motors.find(
        (m) =>
          m.brand.trim() === brand &&
          (m.model ?? '').trim() === model &&
          (!editing || m.id !== editing.id),
      );
      if (dup) {
        messageApi.error(t('library.errors.duplicateModel', { brand, model }));
        return;
      }
    }
    const draft: MotorDraft = {
      brand,
      ...(series ? { series } : { series: '' }),
      ...(model ? { model } : {}),
      ...(frameSize ? { frameSize } : {}),
      power: values.power,
      ratedTorque: values.ratedTorque,
      ratedSpeed: values.ratedSpeed,
      hasBrakeOption: values.hasBrakeOption,
      cpqMotorBrandCode: values.cpqMotorBrandCode,
      cpqMotorPowerCode: values.cpqMotorPowerCode,
    };
    if (editing) {
      update(editing.id, draft);
      messageApi.success(t('library.updateSuccess'));
    } else {
      add(draft);
      messageApi.success(t('library.addSuccess'));
    }
    closeModal();
  };

  const handleExport = () => {
    const json = exportJson();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `motor-library-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    messageApi.success(t('library.exportSuccess'));
  };

  // 机座筛选候选：从当前 motors 收集 unique frameSize（非空，按字符串自然顺序）
  const frameFilters = Array.from(
    new Set(motors.map((m) => (m.frameSize ?? '').trim()).filter(Boolean)),
  )
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    .map((f) => ({ text: f, value: f }));

  const columns: ColumnsType<Motor> = [
    {
      title: t('library.table.brand'),
      dataIndex: 'brand',
      key: 'brand',
      width: 100,
      render: (b: string) => <Tag color="blue">{b}</Tag>,
    },
    {
      title: t('library.table.series'),
      dataIndex: 'series',
      key: 'series',
      width: 140,
    },
    {
      title: t('library.table.model'),
      dataIndex: 'model',
      key: 'model',
      width: 180,
      render: (m?: string) => (m ? <Text code>{m}</Text> : <Text type="secondary">—</Text>),
    },
    {
      title: t('library.table.frameSize'),
      dataIndex: 'frameSize',
      key: 'frameSize',
      width: 100,
      filters: frameFilters.length > 0 ? frameFilters : undefined,
      onFilter: (value, m) => (m.frameSize ?? '') === value,
      render: (f?: string) => (f ? <Tag>{f}</Tag> : <Text type="secondary">—</Text>),
    },
    {
      title: t('library.table.power'),
      dataIndex: 'power',
      key: 'power',
      width: 90,
      sorter: (a, b) => a.power - b.power,
      render: (w: number) => <Text strong>{formatPower(w)}</Text>,
    },
    {
      title: t('library.table.ratedTorque'),
      dataIndex: 'ratedTorque',
      key: 'ratedTorque',
      width: 110,
      render: (tq: number) => `${tq.toFixed(2)} N·m`,
    },
    {
      title: t('library.table.ratedSpeed'),
      dataIndex: 'ratedSpeed',
      key: 'ratedSpeed',
      width: 110,
      render: (r: number) => `${r} rpm`,
    },
    {
      title: t('library.table.brake'),
      dataIndex: 'hasBrakeOption',
      key: 'hasBrakeOption',
      width: 70,
      render: (b: boolean) =>
        b ? (
          <Tag color="green">{t('library.table.brakeAvail')}</Tag>
        ) : (
          <Tag>{t('library.table.brakeNone')}</Tag>
        ),
    },
    {
      title: t('library.table.cpqCode'),
      key: 'cpq',
      width: 120,
      render: (_, m) => (
        <Text code>
          {m.cpqMotorBrandCode}
          {m.cpqMotorPowerCode}
        </Text>
      ),
    },
    {
      title: t('common.operations'),
      key: 'ops',
      width: 140,
      render: (_, m) => (
        <Space size="small">
          <Button size="small" type="link" onClick={() => openEdit(m)}>
            {t('common.edit')}
          </Button>
          <Popconfirm
            title={t('common.confirmDelete')}
            description={t('library.deleteConfirmDesc')}
            onConfirm={() => {
              remove(m.id);
              messageApi.success(t('library.deleteSuccess'));
            }}
          >
            <Button size="small" type="link" danger>
              {t('common.delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {messageCtx}
      <Card
        title={t('library.cardTitle')}
        size="small"
        extra={
          <Space>
            <Tooltip title={t('library.exportTip')}>
              <Button onClick={handleExport}>{t('library.exportJson')}</Button>
            </Tooltip>
            <Popconfirm
              title={t('library.resetConfirmTitle')}
              description={t('library.resetConfirmDesc')}
              onConfirm={() => {
                resetDefault();
                messageApi.success(t('library.resetSuccess'));
              }}
              disabled={!customized}
            >
              <Button disabled={!customized}>{t('library.resetDefault')}</Button>
            </Popconfirm>
            <Button type="primary" onClick={openCreate}>
              {t('library.addMotor')}
            </Button>
          </Space>
        }
      >
        <Alert
          type={customized ? 'warning' : 'info'}
          showIcon
          style={{ marginBottom: 12 }}
          message={
            customized ? t('library.alertCustomized') : t('library.alertDefault')
          }
        />
        <Table<Motor>
          size="small"
          rowKey="id"
          dataSource={motors}
          columns={columns}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Modal
        title={
          editing
            ? t('library.modal.editTitle', {
                label: `${editing.brand} ${editing.series} ${formatPower(editing.power)}`,
              })
            : t('library.modal.addTitle')
        }
        open={creating || editing !== null}
        onCancel={closeModal}
        onOk={() => form.submit()}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        width={680}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={EMPTY_FORM}
        >
          <Row gutter={12}>
            <Col xs={24} sm={8}>
              <Form.Item
                label={t('library.modal.brand')}
                name="brand"
                rules={[{ required: true, message: t('common.required') }]}
              >
                <Input placeholder={t('library.modal.brandPh')} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label={t('library.modal.series')} name="series">
                <Input placeholder={t('library.modal.seriesPh')} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label={t('library.modal.model')}
                name="model"
                tooltip={t('library.modal.modelTip')}
              >
                <Input placeholder={t('library.modal.modelPh')} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                label={t('library.modal.frameSize')}
                name="frameSize"
                tooltip={t('library.modal.frameSizeTip')}
              >
                <Input placeholder={t('library.modal.frameSizePh')} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label={t('library.modal.power')}
                name="power"
                rules={[
                  { required: true, message: t('common.required') },
                  { type: 'number', min: 1, message: t('common.gtZero') },
                ]}
              >
                <InputNumber style={{ width: '100%' }} step={100} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item
                label={t('library.modal.ratedTorque')}
                name="ratedTorque"
                rules={[
                  { required: true, message: t('common.required') },
                  { type: 'number', min: 0.0001, message: t('common.gtZero') },
                ]}
              >
                <InputNumber style={{ width: '100%' }} step={0.1} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item
                label={t('library.modal.ratedSpeed')}
                name="ratedSpeed"
                rules={[
                  { required: true, message: t('common.required') },
                  { type: 'number', min: 1, message: t('common.gtZero') },
                ]}
              >
                <InputNumber style={{ width: '100%' }} step={100} />
              </Form.Item>
            </Col>

            <Col xs={12} sm={8}>
              <Form.Item
                label={t('library.modal.cpqBrand')}
                name="cpqMotorBrandCode"
                tooltip={t('library.modal.cpqBrandTip')}
                rules={[{ required: true, message: t('common.required') }]}
              >
                <Input placeholder="W" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item
                label={t('library.modal.cpqPower')}
                name="cpqMotorPowerCode"
                tooltip={t('library.modal.cpqPowerTip')}
                rules={[{ required: true, message: t('common.required') }]}
              >
                <Input placeholder="750W" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label={t('library.modal.hasBrake')}
                name="hasBrakeOption"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}
